import type { OrthographicCamera } from 'three';

/**
 * World-units-per-screen-pixel for an orthographic projection: one world unit
 * spans `canvasHeightPx * zoom / frustumHeight` pixels, so one pixel is the
 * inverse. Inputs are clamped so a camera caught mid-resize, or before a
 * viewport is set, cannot return Infinity.
 *
 * Take this overload for values off reactive `CameraState` + `viewportSize`;
 * take {@link worldPerScreenPx} for a live camera.
 */
export function orthoWorldPerScreenPx(args: {
    frustumHeight: number;
    canvasHeightPx: number;
    zoom: number;
}): number {
    const frustumHeight = Math.max(args.frustumHeight, 1e-6);
    return frustumHeight / (Math.max(args.canvasHeightPx, 1) * Math.max(args.zoom, 1e-6));
}

/**
 * As {@link orthoWorldPerScreenPx}, for a live camera. Use for pixel-tolerance
 * hit-testing and for sizing nodes that must hold a constant size on screen.
 */
export function worldPerScreenPx(camera: OrthographicCamera, canvasHeightPx: number): number {
    return orthoWorldPerScreenPx({
        frustumHeight: camera.top - camera.bottom,
        canvasHeightPx,
        zoom: camera.zoom,
    });
}
